/**
 * Project Bobcat — a new solution's Supervisor and Knowledge Agent, swapped
 * for the AI-Native ones. Every new Supervisor solution gets them.
 *
 * Create (the backend) builds both agents from Gemini scripts, in nodes
 * 7000–7999 (and the five-digit 70000s it numbers helper steps with). The
 * AI-Native template (`public/templates/ai-native/supervisor-knowledge.csv`)
 * uses the same numbers for the same jobs, with AI Gateway model calls
 * (`CallAIModel`) and the platform's own Supervisor routing. Its prompts are
 * written into the nodes, not loaded from Langfuse, so it runs in every
 * environment (the runtime there may not have the `langfuse` package). This
 * plans the swap; the store applies it. Pure.
 *
 * What stays: everything outside those numbers — Start, error handling, and
 * the task agents Create builds. Their routes into the old agents move to the
 * template's equivalents (`REWIRE`): a person to the template's escalation
 * (999), frustration to its frustration step, the old Knowledge answer to the
 * template's Knowledge Agent. The template's own 999 and 6661 are added only
 * when the solution has no node with that number.
 *
 * What carries over from the old agents: the brand and solution names the
 * old Supervisor set for its prompt (`BRAND_NAME_FOR_LLM`,
 * `SOLUTION_NAME_FOR_LLM`), and the knowledge base's corpus ids. A solution
 * created without a knowledge base gets no RAG search: the template's
 * search step is dropped and its query step goes straight to the answer.
 */

import {
  findEscalationRow,
  handoffOffFields,
} from '../../utils/live-agent-handoff';

export interface SwapRow {
  id: string;
  message?: unknown;
  description?: unknown;
  nodeNumber?: number | null;
  nodeType?: unknown;
  command?: unknown;
  nodeName?: unknown;
  nextNodes?: number[] | null;
  whatsNext?: string[] | null;
  richAssetContent?: unknown;
  parameterInput?: unknown;
  order?: number;
  solutionVersionId?: string;
  settings?: unknown;
}

export interface AiNativeSwapOptions {
  /** A fresh id per added row. */
  newId: () => string;
  /** What the brand is, for the Supervisor's prompt (`BRAND_DESCRIPTION`). */
  brandDescription?: string;
  /** Create skipped the knowledge base: no RAG search to keep. */
  knowledgeSkipped?: boolean;
  /**
   * Keep the search step even with no corpus to point it at: demo knowledge
   * (`withDemoKnowledge`) replaces it, and needs it there to replace.
   */
  keepSearch?: boolean;
  /**
   * Demo only (`FE_BOBCAT_DEMO_CORPUS_ID`): the corpus every new solution's
   * Knowledge Agent searches, in place of the one Create made (or none).
   */
  corpusOverride?: string;
  /**
   * The project has no live agent platform: the escalation step says no one
   * is available instead of transferring (`utils/live-agent-handoff.ts`).
   */
  noLiveAgent?: boolean;
}

/** The node numbers Create's Supervisor and Knowledge Agent use. */
export const isAgentNodeNumber = (n: number | null | undefined): boolean =>
  typeof n === 'number' &&
  ((n >= 7000 && n <= 7999) || (n >= 70000 && n <= 79999));

const GROUP = 'g';
const isGroup = (row: SwapRow) => String(row.nodeType).toLowerCase() === GROUP;

/** The old agents' steps, by number, that kept rows may still route to. */
const REWIRE: Record<number, number> = {
  7300: 7200, // Knowledge Agent answer → the template's Knowledge Agent
  7900: 999, // escalation → the template's escalation
  7901: 999,
  7902: 999,
  7910: 7130, // frustration → the template's frustration step
};
/** Any other old step a kept row routes to: back through the Supervisor. */
const SUPERVISOR_ENTRY = 7000;

const RAG_SEARCH = /GetRAGSearchResponse$/;
const QUERY_STEP = 7200;
const SEARCH_STEP = 7210;
const ANSWER_STEP = 7300;
const VARS_STEP = 7005;

const parseParam = (raw: unknown): Record<string, unknown> | null => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : null;
  } catch {
    return null;
  }
};

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** The old Supervisor is there to swap: Create's Gemini routing. */
export const hasGeneratedSupervisor = (rows: SwapRow[]): boolean =>
  rows.some(
    (r) =>
      isAgentNodeNumber(r.nodeNumber) &&
      /^(SupervisorRouting|GetGeminiCompletion)/.test(String(r.command ?? ''))
  );

/** Brand and solution names the old Supervisor set for its prompt. */
const brandOf = (rows: SwapRow[]) => {
  const names = { brand: '', solution: '' };
  rows.forEach((r) => {
    if (!isAgentNodeNumber(r.nodeNumber)) return;
    const set = parseParam(r.parameterInput)?.set as
      | Record<string, unknown>
      | undefined;
    if (!set) return;
    names.brand ||= text(set.BRAND_NAME_FOR_LLM);
    names.solution ||= text(set.SOLUTION_NAME_FOR_LLM);
  });
  return names;
};

/** The old knowledge base's corpus ids, from its RAG search. */
const corpusOf = (rows: SwapRow[]) => {
  const search = rows.find(
    (r) =>
      isAgentNodeNumber(r.nodeNumber) &&
      RAG_SEARCH.test(String(r.command ?? ''))
  );
  const p = parseParam(search?.parameterInput);
  if (!p) return null;
  return {
    live: text(p.live_corpus_id),
    sandbox: text(p.sandbox_corpus_id),
  };
};

const target = (n: number, existing: Set<number>, added: Set<number>) => {
  if (!isAgentNodeNumber(n)) return n;
  if (REWIRE[n] !== undefined) return REWIRE[n];
  return added.has(n) || existing.has(n) ? n : SUPERVISOR_ENTRY;
};

/** `label~7900|other~7001` style routes, each number moved. */
const rewireRoutes = (value: string, move: (n: number) => number) =>
  value.replace(/~(-?\d+)/g, (_, n: string) => `~${move(Number(n))}`);

/**
 * The rows after the swap, or null when there's no Create-built Supervisor
 * to swap (a blank solution, or one already swapped). `template` is the
 * template's rows as `normalizeAllNodes` reads them (no ids yet).
 */
export const aiNativeSwapRows = <R extends SwapRow>(
  rows: R[],
  template: SwapRow[],
  options: AiNativeSwapOptions
): R[] | null => {
  if (!hasGeneratedSupervisor(rows) || !template.length) return null;

  const old = rows.filter(
    (r) => !isGroup(r) && isAgentNodeNumber(r.nodeNumber)
  );
  const removed = new Set(old.map((r) => r.id));
  const kept = rows.filter((r) => !removed.has(r.id));
  const keptNumbers = new Set(
    kept
      .map((r) => r.nodeNumber)
      .filter((n): n is number => typeof n === 'number')
  );
  const { brand, solution } = brandOf(old);
  const override = options.corpusOverride?.trim();
  const corpus = override
    ? { live: override, sandbox: override }
    : corpusOf(old);
  const searchless =
    !options.keepSearch && (options.knowledgeSkipped || !corpus);
  const solutionVersionId = rows.find(
    (r) => r.solutionVersionId
  )?.solutionVersionId;

  // The template's rows to add: its agents, plus 999/6661 when missing.
  const adding = template.filter(
    (t) =>
      typeof t.nodeNumber === 'number' &&
      (isAgentNodeNumber(t.nodeNumber)
        ? !(searchless && t.nodeNumber === SEARCH_STEP)
        : !keptNumbers.has(t.nodeNumber))
  );
  const addedNumbers = new Set(adding.map((t) => t.nodeNumber as number));
  const move = (n: number) => target(n, keptNumbers, addedNumbers);

  const added = adding.map((t) => {
    const row = {
      ...structuredClone(t),
      id: options.newId(),
      ...(solutionVersionId ? { solutionVersionId } : {}),
    } as SwapRow;
    const params = parseParam(row.parameterInput);
    if (row.nodeNumber === VARS_STEP && params?.set) {
      const set = params.set as Record<string, unknown>;
      row.parameterInput = {
        ...params,
        set: {
          ...set,
          BRAND_NAME: brand || solution,
          SOLUTION_NAME: solution || brand,
          BRAND_DESCRIPTION: options.brandDescription?.trim() || brand,
        },
      };
    }
    if (row.nodeNumber === SEARCH_STEP && params && corpus) {
      row.parameterInput = {
        ...params,
        live_corpus_id: corpus.live,
        sandbox_corpus_id: corpus.sandbox || corpus.live,
      };
    }
    // Without a search step, what led to it goes to the answer.
    if (searchless && row.whatsNext) {
      row.whatsNext = row.whatsNext.map((w) =>
        w.replace(new RegExp(`~${SEARCH_STEP}$`), `~${ANSWER_STEP}`)
      );
    }
    if (searchless && row.nextNodes) {
      row.nextNodes = row.nextNodes.map((n) =>
        n === SEARCH_STEP ? ANSWER_STEP : n
      );
    }
    return row as R;
  });

  const rewired = kept.map((r) => {
    let next = r;
    if (r.nextNodes?.some(isAgentNodeNumber)) {
      next = { ...next, nextNodes: r.nextNodes.map(move) };
    }
    if (r.whatsNext?.some((w) => /~(7\d{3}|7\d{4})$/.test(w))) {
      next = {
        ...next,
        whatsNext: r.whatsNext.map((w) => rewireRoutes(w, move)),
      };
    }
    if (
      typeof r.richAssetContent === 'string' &&
      /~(7\d{3}|7\d{4})\b/.test(r.richAssetContent)
    ) {
      next = {
        ...next,
        richAssetContent: rewireRoutes(r.richAssetContent, move),
      };
    }
    return next;
  });

  // The template's agents go where the old ones were; anything it adds
  // outside them (999, 6661) goes at the end. Order follows the array.
  const firstOld = rows.findIndex((r) => removed.has(r.id));
  const at = rows
    .slice(0, firstOld < 0 ? rows.length : firstOld)
    .filter((r) => !removed.has(r.id)).length;
  const agents = added.filter((r) => isAgentNodeNumber(r.nodeNumber));
  const extras = added.filter((r) => !isAgentNodeNumber(r.nodeNumber));
  const result = [
    ...rewired.slice(0, at),
    ...agents,
    ...rewired.slice(at),
    ...extras,
  ];
  const ordered = result.map((r, i) =>
    r.order === i + 1 ? r : { ...r, order: i + 1 }
  );
  if (!options.noLiveAgent) return ordered;
  const escalation = findEscalationRow(ordered);
  return escalation
    ? ordered.map((r) =>
        r === escalation ? ({ ...r, ...handoffOffFields(r) } as R) : r
      )
    : ordered;
};

interface CorpusIds {
  live: string;
  sandbox: string;
}

/**
 * The rows with the RAG search pointed at `corpus`, when their own corpus id
 * is still empty. Unchanged otherwise.
 */
const withCorpus = <R extends SwapRow>(
  rows: R[],
  corpus: CorpusIds | null
): R[] => {
  const local = corpusOf(rows);
  if (!local || local.live || !corpus?.live) return rows;
  return rows.map((r) => {
    if (
      !isAgentNodeNumber(r.nodeNumber) ||
      !RAG_SEARCH.test(String(r.command ?? ''))
    ) {
      return r;
    }
    const p = parseParam(r.parameterInput) ?? {};
    return {
      ...r,
      parameterInput: {
        ...p,
        live_corpus_id: corpus.live,
        sandbox_corpus_id: corpus.sandbox || corpus.live,
      },
    };
  });
};

/**
 * The rows with the knowledge base's corpus ids as the server has them. The
 * backend writes `live_corpus_id` onto the RAG search some time after
 * Create; the rows loaded before that still hold `""`. Swapping from those
 * would write `""` over the id the backend just set, and the first publish,
 * which waits for it, would never come. Unchanged when the server has no id
 * yet (the backend then fills the swapped search) or the rows already do.
 */
export const withServerCorpus = <R extends SwapRow>(
  rows: R[],
  serverNodes: { parameterInput?: unknown }[]
): R[] => {
  const server = serverNodes
    .map((n) => parseParam(n.parameterInput))
    .find((p) => text(p?.live_corpus_id));
  return withCorpus(
    rows,
    server
      ? {
          live: text(server.live_corpus_id),
          sandbox: text(server.sandbox_corpus_id),
        }
      : null
  );
};

/**
 * Demo only: ready support information in place of the knowledge-base
 * search (`FE_BOBCAT_DEMO_KNOWLEDGE`, set in a local `.env.local`), for
 * environments whose RAG search can't run (candidate: every search 404s) —
 * the way the Bobcat Demo solution carries its help center. The search step
 * sets its result to `knowledge`, so the answer step reads the same variable
 * it would have. The step that writes the search query goes straight to it:
 * with nothing to search, that model call is only delay (on candidate it
 * takes 5–30 s and sometimes times out, escalating the chat). Unchanged
 * without text or a search.
 */
export const withDemoKnowledge = <R extends SwapRow>(
  rows: R[],
  knowledge: string | null | undefined
): R[] => {
  const body = knowledge?.trim();
  const isSearch = (r: SwapRow) =>
    isAgentNodeNumber(r.nodeNumber) && RAG_SEARCH.test(String(r.command ?? ''));
  const search = rows.find(isSearch);
  if (!body || !search) return rows;
  const saveAs =
    text(parseParam(search.parameterInput)?.save_output_as) ||
    'RAG_SEARCH_RESPONSE';
  const routes = (r: SwapRow) =>
    r.whatsNext?.filter((w) => /^(true|false)~/.test(w));
  return rows.map((r) => {
    if (isSearch(r)) {
      return {
        ...r,
        command: 'SysAssignVariable',
        nodeName:
          'NLX - Knowledge - demo support information (SysAssignVariable)',
        message: 'Set the demo support information',
        description:
          'Demo only: sets the support information the answer reads.',
        parameterInput: { set: { [saveAs]: body } },
        whatsNext: routes(r),
      } as R;
    }
    if (r.nodeNumber === QUERY_STEP) {
      return {
        ...r,
        command: 'SysAssignVariable',
        nodeName: 'NLX - Knowledge - demo: no search query (SysAssignVariable)',
        message: 'Skip the search query',
        description:
          'Demo only: nothing to search, so go straight to the support information.',
        parameterInput: { set: { TOPICS_FOR_CLARIFICATION: 'none' } },
        variables: ['TOPICS_FOR_CLARIFICATION'],
        whatsNext: [`true~${search.nodeNumber}`, 'false~999'],
      } as R;
    }
    return r;
  });
};
